import React, { useCallback, useEffect, useState } from 'react'
import { supabase, parseRpcError } from '@/lib/supabaseClient'
import type { PortalHotel } from '@/lib/types'
import RoomTree, { GuestWriteResult } from '@/components/RoomTree'
import { PageSpinner } from '@/components/ui'

export default function RoomingListTab({ companyId }: { companyId: string }) {
  const [hotels, setHotels] = useState<PortalHotel[] | null>(null)

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('admin_company_room_tree', { p_company_id: companyId })
    if (!error) setHotels((data as PortalHotel[]) || [])
  }, [companyId])

  useEffect(() => {
    load()
  }, [load])

  if (hotels === null) return <PageSpinner label="Loading rooming list…" />

  async function saveGuest(guestId: string, fields: { full_name: string; mobile: string; email?: string | null }, expectedUpdatedAt: string): Promise<GuestWriteResult> {
    const { data, error } = await supabase.rpc('admin_update_guest', {
      p_guest_id: guestId,
      p_full_name: fields.full_name || null,
      p_mobile: fields.mobile || null,
      p_email: fields.email || null,
      p_check_in: null,
      p_check_out: null,
      p_notes: null,
      p_expected_updated_at: expectedUpdatedAt,
      p_bypass_duplicate: false,
    })
    if (error) {
      const { code, detail } = parseRpcError(error)
      return { ok: false, code, detail }
    }
    return { ok: true, updated: { updated_at: (data as any).updated_at } }
  }

  async function clearGuest(guestId: string, expectedUpdatedAt: string): Promise<GuestWriteResult> {
    const { data, error } = await supabase.rpc('admin_clear_guest', { p_guest_id: guestId, p_expected_updated_at: expectedUpdatedAt })
    if (error) {
      const { code, detail } = parseRpcError(error)
      return { ok: false, code, detail }
    }
    return { ok: true, updated: { updated_at: (data as any).updated_at } }
  }

  async function moveGuest(fromId: string, toId: string): Promise<GuestWriteResult> {
    const { error } = await supabase.rpc('admin_move_guest', { p_from_guest_id: fromId, p_to_guest_id: toId, p_swap: false })
    if (error) {
      const { code, detail } = parseRpcError(error)
      return { ok: false, code, detail }
    }
    return { ok: true }
  }

  async function saveRoom(
    roomId: string,
    fields: { physical_room_number: string | null; check_in: string | null; check_out: string | null; board_type: string | null }
  ): Promise<GuestWriteResult> {
    const room = hotels?.flatMap((h) => h.room_types.flatMap((rt) => rt.rooms)).find((r) => r.id === roomId)
    const { error } = await supabase.rpc('admin_update_room', {
      p_room_id: roomId,
      p_physical_room_number: fields.physical_room_number,
      p_notes: room?.notes ?? null,
      p_admin_notes: (room as any)?.admin_notes ?? null,
      p_check_in: fields.check_in,
      p_check_out: fields.check_out,
      p_board_type: fields.board_type,
    })
    if (error) {
      const { code, detail } = parseRpcError(error)
      return { ok: false, code, detail }
    }
    return { ok: true }
  }

  async function removeRoom(roomId: string): Promise<GuestWriteResult> {
    const { error } = await supabase.rpc('admin_remove_room', { p_room_id: roomId })
    if (error) {
      const { code, detail } = parseRpcError(error)
      return { ok: false, code, detail }
    }
    return { ok: true }
  }

  return (
    <RoomTree
      hotels={hotels}
      showPhysicalRoomNumbers={true}
      isAdmin
      canFill
      canEdit
      canClear
      canMove
      onSaveGuest={saveGuest}
      onClearGuest={clearGuest}
      onMoveGuest={moveGuest}
      onSaveRoom={saveRoom}
      onRemoveRoom={removeRoom}
      onDataChanged={load}
    />
  )
}
